import { CircleAlert, ExternalLink, ShieldAlert } from 'lucide-react'

/** Maps tool action to the UI page where the user can perform it manually. */
const HUMAN_ACTION_LINKS: Record<string, { label: string; path: string }> = {
  approvePolicy: { label: 'Go to policy', path: '../policies' },
  acceptRisk: { label: 'Go to risk register', path: '../risks' },
  falsePositive: { label: 'Go to remediation', path: '../remediation' },
  reviewSubmission: { label: 'Go to evidence', path: '../evidence' },
}

/** Maps tool names to actionable recovery guidance when they fail. */
const TOOL_ERROR_GUIDANCE: Record<string, { message: string; link?: { label: string; path: string } }> = {
  runGuidedTemplate: {
    message: 'Cloud scan couldn\'t complete. Check your integration is connected and credentials are valid.',
    link: { label: 'Settings > Integrations', path: '../settings/integrations' },
  },
  getCloudScanEvidence: {
    message: 'Couldn\'t load cloud scan data. Verify your cloud provider is connected.',
    link: { label: 'Settings > Integrations', path: '../settings/integrations' },
  },
  delegateToScannerAgent: {
    message: 'Scanner agent failed. This may be a temporary issue. Try again or check your integration setup.',
    link: { label: 'Settings > Integrations', path: '../settings/integrations' },
  },
  generatePolicy: {
    message: 'Policy generation failed. Try regenerating or check that the policy outline is complete.',
  },
  updatePolicySection: {
    message: 'Section update failed. The document may have changed. Try regenerating the full policy.',
  },
  checkPolicyCompliance: {
    message: 'Compliance check couldn\'t complete. Try again or regenerate the policy first.',
  },
  buildPolicyPlan: {
    message: 'Couldn\'t build the policy plan. Ensure evidence sources are available.',
  },
  scanPolicyEvidence: {
    message: 'Evidence scan timed out. Try again or check that integrations are connected.',
    link: { label: 'Settings > Integrations', path: '../settings/integrations' },
  },
  getPopulationLists: {
    message: 'Couldn\'t load population lists. Verify your cloud integrations are set up.',
    link: { label: 'Settings > Integrations', path: '../settings/integrations' },
  },
}

function GuidanceCard({ title, description, link, variant = 'info' }: {
  title: string
  description: string
  link?: { label: string; path: string }
  variant?: 'info' | 'warning'
}) {
  const isInfo = variant === 'info'
  return (
    <div className={`flex items-center gap-2 text-xs ${
      isInfo ? 'px-3 py-2 rounded-md border border-status-blue-border bg-status-blue-bg' : ''
    }`}>
      {isInfo
        ? <ShieldAlert className="h-3.5 w-3.5 text-status-blue shrink-0" />
        : <CircleAlert className="h-3.5 w-3.5 text-status-orange-strong shrink-0" />
      }
      <div className="space-y-1 min-w-0">
        <p className={`font-medium ${isInfo ? 'text-status-blue-strong' : 'text-text-body'}`}>{title}</p>
        {description && (
        <p className={isInfo ? 'text-status-blue-strong' : 'text-text-subtle'}>
          {description}
          {link && (
            <a
              href={link.path}
              className={`inline-flex items-center gap-0.5 ml-1 underline underline-offset-2 font-medium ${
                isInfo ? 'text-status-blue-strong hover:text-status-blue-text' : 'text-cobalt-strong hover:text-cobalt-strong/80'
              }`}
            >
              {link.label}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </p>
        )}
      </div>
    </div>
  )
}

interface ToolErrorGuidanceProps {
  toolName: string
  errorMessage?: string | null
  errorCode?: string | null
  toolAction?: string | null
}

/**
 * Contextual error recovery guidance shown when a tool invocation fails.
 * Routes to specific card variants based on errorCode.
 */
export function ToolErrorGuidance({ toolName, errorMessage, errorCode, toolAction }: ToolErrorGuidanceProps) {
  if (errorCode === 'INTEGRATION_READ_ONLY') {
    return (
      <GuidanceCard
        title="Read-only integration"
        description={errorMessage ?? 'This integration only has read access.'}
        link={{ label: 'Settings > Integrations', path: '../integrations' }}
      />
    )
  }

  if (errorCode === 'REQUIRES_HUMAN_ACTION') {
    const link = toolAction ? HUMAN_ACTION_LINKS[toolAction] : undefined
    return (
      <GuidanceCard
        title="This action requires your approval"
        description="Vera can't perform this automatically. It needs a human decision."
        link={link}
      />
    )
  }

  const guidance = TOOL_ERROR_GUIDANCE[toolName]

  return (
    <GuidanceCard
      variant="warning"
      title={errorMessage ?? guidance?.message ?? 'Something went wrong. Try again or rephrase your request.'}
      description={guidance && errorMessage ? guidance.message : ''}
      link={guidance?.link}
    />
  )
}
